import { isVerticalCategory } from '@kijiji/category'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { type R2SProps, R2S } from '@/features/listing/components/reply-to-seller/R2S'
import { R2SModalFormWrapper } from '@/features/listing/components/reply-to-seller/styled'
import { useReplyToSellerModal } from '@/features/listing/hooks/reply-to-seller/useReplyToSellerModal'
import { getPriceClassification } from '@/features/listing/utils/getPriceClassification'
import { isDealerListing } from '@/features/listing/utils/isDealerListing'
import { isPintListing } from '@/features/listing/utils/isPintListing'
import { isSM360Listing } from '@/features/listing/utils/isSM360Listing'
import { getR2SBaseTrackingLabel } from '@/features/listing/utils/reply-to-seller/getR2SBaseTrackingLabel'
import { isGuestMessageEnabled } from '@/features/listing/utils/reply-to-seller/isGuestMessageEnabled'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { Modal } from '@/ui/molecules/modal/Modal'

type R2SModalProps = Omit<R2SProps, 'trackingPlacement'>

/**
 * Modal wrapper for R2S. Encapsulates the R2S message form.
 *
 * To see all the props that can be passed to the form, see the R2SForm component.
 *
 * @see {@link R2SForm} for the rest of the props that can be passed.
 */
export const R2SModal: FC<R2SModalProps> = ({ ...r2sProps }) => {
  const { status } = useSession()
  const { isOpen, handleCloseModal, placement } = useReplyToSellerModal()

  const { spacing } = useTheme()
  const { t } = useTranslation([TRANSLATION_KEYS.COMMON])

  if (!isOpen) return null

  const handleCloseR2SModal = () => {
    const { listing } = r2sProps

    const isPintAd = isPintListing(listing)
    const isSM360Ad = isSM360Listing(listing)
    const isDealerAd = isDealerListing(listing.attributes)
    const isVerticalAd = isVerticalCategory(listing.categoryId)

    const priceRate = getPriceClassification(listing.price)?.rating

    const label = getR2SBaseTrackingLabel({
      categoryId: listing.categoryId,
      placement,
      isUserAuthenticated: isUserAuthenticated(status),
      isGuestMessageEnabled: isGuestMessageEnabled({
        isDealerAd,
        isPintAd,
        isSM360Ad,
        isVerticalAd,
      }),
      priceRate,
    })

    trackEvent({ action: GA_EVENT.R2SEmailCancel, label })
    handleCloseModal()
  }

  return (
    <Modal
      closeButtonLabel={t('common:modals.close.button.label')}
      hasCloseButton
      id="r2s-message-form-modal"
      isOpen={isOpen}
      label={t('common:feedback:modal.feedback.title')}
      padding={{ small: `${spacing.giant} 0 0` }}
      onCancel={handleCloseR2SModal}
    >
      <R2SModalFormWrapper>
        <R2S {...r2sProps} trackingPlacement={placement} isModal={true} />
      </R2SModalFormWrapper>
    </Modal>
  )
}
